// utils/avatar.js
// Generates a base64 PNG with white initials on a black background

import logger from './logger';
import indexedDBService from '../services/indexedDB';
import { acquireAvatar } from './avatarManager';

/**
 * Blob URL Cache Manager
 * Prevents memory leaks by tracking and revoking blob URLs
 */
const blobUrlCache = new Map(); // userId -> { url: string, timestamp: number }
const inFlightRequests = new Map(); // userId -> Promise

/**
 * Revoke blob URL for a specific user
 * @param {string} userId - Matrix user ID
 */
export function revokeUserAvatarBlobUrl(userId) {
  const cached = blobUrlCache.get(userId);
  if (cached) {
    URL.revokeObjectURL(cached.url);
    blobUrlCache.delete(userId);
    logger.debug(`Revoked blob URL for ${userId}`);
  }
}

/**
 * Revoke all cached blob URLs
 * Call this on logout or when cleaning up
 */
export function revokeAllAvatarBlobUrls() {
  logger.debug(`Revoking ${blobUrlCache.size} cached blob URLs`);
  for (const [userId, { url }] of blobUrlCache.entries()) {
    URL.revokeObjectURL(url);
  }
  blobUrlCache.clear();
  logger.debug('All blob URLs revoked');
}

/**
 * Generate a base64 PNG avatar with initials
 * @param {string} name - The name to extract initials from
 * @param {number} [size=64] - The width/height of the image
 * @returns {string} - base64 data URL
 */
export function generateInitialsAvatar(name, size = 64) {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');

  // Draw black background
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, size, size);

  // Get initials
  const initials = (name || '?')
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  // Draw initials
  ctx.font = `${size * 0.5}px sans-serif`;
  ctx.fillStyle = '#fff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(initials, size / 2, size / 2);

  return canvas.toDataURL('image/png');
}

/**
 * Get real avatar HTTP URL from Matrix user
 * @param {MatrixClient} client - The Matrix client instance
 * @param {string} userId - The Matrix user ID
 * @param {number} [size=96] - The desired avatar size
 * @returns {string|null} - HTTP URL of avatar or null if not available
 */
export function getUserAvatarUrl(client, userId, size = 96) {
  if (!client || !userId) {
    return null;
  }

  try {
    // Get the user object from Matrix client
    const user = client.getUser(userId);

    if (!user) {
      return null;
    }

    // Check if user has an avatar URL (MXC URL)
    const mxcUrl = user.avatarUrl;

    if (!mxcUrl) {
      return null;
    }

    // Convert MXC URL to HTTP URL
    // Using Matrix v1 authenticated media endpoints (Matrix 1.11+ standard)
    const httpUrl = client.mxcUrlToHttp(
      mxcUrl,
      size,     // width
      size,     // height
      'crop',   // resize method
      false,    // allowDirectLinks (security: always false)
      true,     // allowRedirects (required for authenticated media)
      true,      // useAuthentication (required for Matrix 1.11+)
    );

    return httpUrl;
  } catch (error) {
    return null;
  }
}

/**
 * Get avatar for a user - tries real avatar first, falls back to initials
 * @param {MatrixClient} client - The Matrix client instance
 * @param {string} userId - The Matrix user ID
 * @param {string} displayName - The user's display name (for initials fallback)
 * @param {number} [size=96] - The desired avatar size
 * @returns {string} - HTTP URL of avatar or data URL of initials
 */
export function getUserAvatar(client, userId, displayName, size = 96) {
  // Try to get real avatar first
  const realAvatar = getUserAvatarUrl(client, userId, size);

  if (realAvatar) {
    return realAvatar;
  }

  // Fallback to initials avatar
  return generateInitialsAvatar(displayName || userId, size);
}

/**
 * Get authenticated avatar as blob URL with IndexedDB caching
 * Includes memory leak prevention via blob URL cache management
 * @param {MatrixClient} client - The Matrix client instance
 * @param {string} userId - The Matrix user ID
 * @param {string} displayName - The user's display name (for initials fallback)
 * @param {number} [size=96] - The desired avatar size
 * @returns {Promise<string>} - Blob URL or data URL of initials avatar
 */
export async function getUserAvatarBlobUrl(client, userId, displayName, size = 96) {
  // Check blob URL cache first (fastest path - already in memory)
  const cachedBlobUrl = blobUrlCache.get(userId);
  if (cachedBlobUrl) {
    return cachedBlobUrl.url;
  }

  // Check if request already in flight to prevent duplicate fetches
  if (inFlightRequests.has(userId)) {
    return await inFlightRequests.get(userId);
  }

  // Create the fetch promise
  const fetchPromise = (async () => {
    try {
    // Step 1: Get MXC URL from Matrix SDK (may be stale if presence disabled)
    const user = client.getUser(userId);
    let mxcUrl = user?.avatarUrl;

    // Step 1b: If MXC URL is missing, fetch from server directly
    // This handles the case where presence is disabled (disablePresence: true)
    // and the User object doesn't have updated profile information
    if (!mxcUrl) {
      try {
        const profile = await client.getProfileInfo(userId);
        mxcUrl = profile.avatar_url;
      } catch (profileError) {
        logger.warn(`Could not fetch profile for ${userId}:`, profileError);
      }
    }

    // If still no MXC URL after trying both methods, fall back to initials
    if (!mxcUrl) {
      return generateInitialsAvatar(displayName || userId, size);
    }

    // Step 2: Convert MXC URL to HTTP URL
    const httpUrl = client.mxcUrlToHttp(
      mxcUrl,
      size,
      size,
      'crop',
      false,  // allowDirectLinks
      true,   // allowRedirects
      true,    // useAuthentication
    );

    if (!httpUrl) {
      return generateInitialsAvatar(displayName || userId, size);
    }

    // Step 3: Check IndexedDB cache
    const cached = await indexedDBService.getCachedAvatar(userId, mxcUrl);

    if (cached && cached.blobData) {

      // Create blob from cached ArrayBuffer
      const blob = new Blob([cached.blobData], { type: cached.mimeType });
      
      // Revoke old blob URL if exists to prevent memory leak
      const oldCached = blobUrlCache.get(userId);
      if (oldCached) {
        URL.revokeObjectURL(oldCached.url);
      }
      
      const blobUrl = URL.createObjectURL(blob);
      
      // Store in blob URL cache for memory management
      blobUrlCache.set(userId, { url: blobUrl, timestamp: Date.now() });

      return blobUrl;
    }

    // Step 4: Cache MISS - fetch from server
    const response = await fetch(httpUrl, {
      headers: {
        'Authorization': `Bearer ${client.getAccessToken()}`,
      },
    });

    if (!response.ok) {
      logger.warn(`Failed to fetch avatar for ${userId}: ${response.status}`);
      return generateInitialsAvatar(displayName || userId, size);
    }

    // Step 5: Convert to blob and ArrayBuffer
    const blob = await response.blob();
    const mimeType = blob.type || 'image/png';

    // Convert blob to ArrayBuffer for IndexedDB storage
    const arrayBuffer = await blob.arrayBuffer();

    // Step 6: Cache in IndexedDB (async, don't block)
    indexedDBService.cacheAvatar(userId, mxcUrl, arrayBuffer, mimeType)
      .catch(err => {
        logger.warn('Failed to cache avatar in IndexedDB:', err);
        // Non-blocking - continue even if caching fails
      });

    // Step 7: Create and return blob URL with memory management
    // Revoke old blob URL if exists to prevent memory leak
    const oldCached = blobUrlCache.get(userId);
    if (oldCached) {
      URL.revokeObjectURL(oldCached.url);
    }
    
    const blobUrl = URL.createObjectURL(blob);
    
    // Store in blob URL cache for memory management
    blobUrlCache.set(userId, { url: blobUrl, timestamp: Date.now() });

    return blobUrl;

    } catch (error) {
      logger.error(`Error fetching authenticated avatar for ${userId}:`, error);
      return generateInitialsAvatar(displayName || userId, size);
    }
  })();

  // Store promise to prevent duplicate in-flight requests
  inFlightRequests.set(userId, fetchPromise);

  try {
    const result = await fetchPromise;
    return result;
  } finally {
    // Remove from in-flight requests when complete
    inFlightRequests.delete(userId);
  }
}

/**
 * Invalidate cached avatar for a user
 * Call this when you know the avatar has changed
 * @param {string} userId - Matrix user ID
 */
export async function invalidateAvatarCache(userId) {
  try {
    logger.debug(`Invalidating avatar cache for: ${userId}`);
    await indexedDBService.deleteCachedAvatar(userId);
  } catch (error) {
    logger.error('Error invalidating avatar cache:', error);
  }
}

/**
 * Clear entire avatar cache
 * Call this on logout or when needed
 * Also revokes all blob URLs to prevent memory leaks
 */
export async function clearAvatarCache() {
  try {
    logger.debug('Clearing entire avatar cache');

    // Revoke all blob URLs to prevent memory leaks
    revokeAllAvatarBlobUrls();

    // Clear IndexedDB cache
    await indexedDBService.clearAvatarCache();
  } catch (error) {
    logger.error('Error clearing avatar cache:', error);
  }
}

/**
 * Clear module-level caches (for testing only)
 * This clears both the blob URL cache and in-flight requests map
 * to prevent cache pollution between tests
 * @private - Test use only
 */
export function __clearCachesForTest() {
  // Revoke all blob URLs
  for (const [userId, { url }] of blobUrlCache.entries()) {
    URL.revokeObjectURL(url);
  }

  // Clear both module-level Maps
  blobUrlCache.clear();
  inFlightRequests.clear();
}

/**
 * Check if an avatar URL is valid and can be displayed
 * @param {string} url - Avatar URL to validate
 * @returns {boolean} True if URL is valid
 */
export const isValidAvatar = (url) => {
  return url && typeof url === 'string' && (
    url.startsWith('data:') ||
    url.startsWith('blob:') ||
    url.startsWith('http://') ||
    url.startsWith('https://') ||
    url.startsWith('/') // Support relative paths for preset avatars
  );
};

/**
 * Get avatar URLs for a room
 *
 * This function handles two scenarios:
 * 1. Rooms with explicit avatars: Returns room avatar at 40px (room display size)
 * 2. DMs without explicit avatars: Returns member avatars at 96px (user avatar size)
 *
 * The different sizes reflect different entity types:
 * - Room avatars: 40px (optimized for room list display)
 * - User avatars in DMs: 96px (reuses cached user avatars)
 *
 * @param {Room} room - Matrix room object
 * @param {string} myUserId - Current user's ID
 * @param {MatrixClient} client - Matrix client
 * @returns {Promise<Array>} Array of avatar descriptors with {url, type, userId/roomId}
 */
export const getRoomAvatars = async (room, myUserId, client) => {
  // Check for explicit room avatar first (e.g., group chat icon)
  try {
    const roomAvatarMxc = room.getMxcAvatarUrl();
    
    if (roomAvatarMxc) {
      // Room has explicit avatar - use it via avatarManager
      const roomName = room.name || 'Group Chat';
      const url = await acquireAvatar(client, room.roomId, roomName, 40, 'room');
      
      // Return descriptor with roomId and type for proper cleanup
      return [{
        roomId: room.roomId,
        url,
        type: 'room',
      }];
    }
  } catch (error) {
    // If getMxcAvatarUrl fails, fall through to member avatars
    logger.warn('Failed to get room avatar:', error);
  }

  // Fall back to member avatars (DM logic) - include invited members
  const allMembers = room.getMembers()
    .filter(m => m.membership === 'join' || m.membership === 'invite');

  if (!allMembers || allMembers.length === 0) return [];

  const getAvatarForMember = async (member) => {
    const displayName = member.rawDisplayName || member.name || member.userId;

    // Acquire avatar from manager (increments ref count)
    const url = await acquireAvatar(client, member.userId, displayName);

    // Return descriptor with userId and type for proper cleanup
    return {
      userId: member.userId,
      url,
      type: 'user',
    };
  };

  let membersToFetch = [];

  switch (allMembers.length) {
    case 1:
      // Single participant - use their avatar
      membersToFetch = [allMembers[0]];
      break;
    case 2:
      // Two participants - use both avatars. Ensure the other user is first.
      membersToFetch = allMembers
        .sort((a, b) => (a.userId === myUserId ? 1 : b.userId === myUserId ? -1 : 0));
      break;
    default: {
      // Multiple participants - use first two other users (excluding current user)
      const otherUsers = allMembers.filter(member => member.userId !== myUserId);
      membersToFetch = otherUsers.slice(0, 2);
      break;
    }
  }

  // Fetch all avatar descriptors in parallel
  const avatarDescriptors = await Promise.all(membersToFetch.map(getAvatarForMember));
  return avatarDescriptors.filter(Boolean);
};
